import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { meetingStore } from 'store/meetingStore';
import { useJoinMeetingByCode } from 'hooks/useMeeting';
import './JoinWithCode.css';

export const JoinWithCode = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { status, data, error: apiError } = useJoinMeetingByCode();
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasHandledSuccess, setHasHandledSuccess] = useState(false);

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
        setCode(value);
        setError('');
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const cleaned = text.toUpperCase().replace(/[^A-Z0-9-]/g, '');
            setCode(cleaned);
            setError('');
        } catch (err) {
            console.error('Failed to read clipboard:', err);
        }
    };

    const validateCode = (code: string): boolean => {
        // Code format: MOI-ABC123 (3 letters + dash + 6 alphanumeric)
        const codeRegex = /^MOI-[A-Z0-9]{6}$/;
        return codeRegex.test(code);
    };

    // API 상태 변화 감지
    useEffect(() => {
        if (status === 'ready' && data && !hasHandledSuccess) {
            setHasHandledSuccess(true);
            const handleSuccess = async () => {
                await showAlert({ message: '모임에 참여하였습니다!' });
                navigate(`/meeting/detail/${data}`, { replace: true });
            };
            handleSuccess();
        } else if (status === 'error' && apiError && !hasHandledSuccess) {
            setHasHandledSuccess(true); // 에러 처리 후 플래그 설정
            setIsLoading(false);
            const errorMessage = (apiError as any)?.response?.data?.message || (apiError as any)?.message || '모임 참여에 실패했습니다.';

            // 특정 에러 케이스 처리
            if (errorMessage.includes('이미') || errorMessage.includes('already')) {
                showAlert({
                    title: '참여 실패',
                    message: '이미 참여 중인 모임입니다.'
                });
            } else if (errorMessage.includes('유효하지') || errorMessage.includes('invalid')) {
                setError('유효하지 않은 초대 코드입니다.');
            } else if (errorMessage.includes('최대') || errorMessage.includes('full')) {
                showAlert({
                    title: '참여 실패',
                    message: '모임 인원이 가득 찼습니다.'
                });
            } else {
                setError(errorMessage);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, data, apiError, hasHandledSuccess]);

    const handleJoin = () => {
        if (!code.trim()) {
            setError('초대 코드를 입력해 주세요.');
            return;
        }

        if (!validateCode(code)) {
            setError('올바른 형식의 코드가 아닙니다. (예: MOI-ABC123)');
            return;
        }

        setIsLoading(true);
        setError('');
        setHasHandledSuccess(false);
        meetingStore.onJoinMeetingByCode(code);
    };

    const isJoinEnabled = code.trim().length > 0 && !isLoading;

    return (
        <div className="join-code-wrapper">
            <Header title="코드로 참여" isSubPage />

            <div className="join-code-content">
                {/* Illustration */}
                <div className="join-code-illustration">
                    <div className="illustration-icon">🎫</div>
                </div>

                {/* Title Section */}
                <div className="join-code-title-section">
                    <h1 className="join-code-title">초대 코드를 입력하세요</h1>
                    <p className="join-code-subtitle">
                        친구에게 받은 초대 코드를 입력하면<br />
                        바로 모임에 참여할 수 있어요.
                    </p>
                </div>

                {/* Code Input Section */}
                <div className="code-input-section">
                    <div className="code-input-container">
                        <input
                            type="text"
                            className={`code-input ${error ? 'error' : ''}`}
                            placeholder="MOI-ABC123"
                            value={code}
                            onChange={handleCodeChange}
                            maxLength={11}
                        />
                        <button className="paste-button" onClick={handlePaste}>
                            📋 붙여넣기
                        </button>
                    </div>

                    {error && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            <span className="error-text">{error}</span>
                        </div>
                    )}

                    <div className="code-format-hint">
                        형식: MOI-XXXXXX (6자리)
                    </div>
                </div>

                {/* Example Section */}
                <div className="example-section">
                    <div className="example-title">💡 초대 코드는 어디서 확인하나요?</div>
                    <ul className="example-list">
                        <li>모임을 만든 사람에게 코드를 받으세요</li>
                        <li>카카오톡, 문자 등으로 공유된 코드를 확인하세요</li>
                        <li>링크로 받았다면 자동으로 참여됩니다</li>
                    </ul>
                </div>
            </div>

            {/* Join Button */}
            <div className="join-code-footer">
                <Button
                    text={isLoading ? "확인 중..." : "참여하기"}
                    disabled={!isJoinEnabled}
                    onPress={handleJoin}
                />
            </div>
        </div>
    );
};
